import React, { useState, useEffect } from 'react';
import { 
  Firestore,
  collection,
  query,
  where,
  getDocs,
  orderBy
} from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../../hooks/AuthProvider';
import LoadingPage from '../../LoadingPage';
import ControlPanel from '../ControlPanel/ControlPanel';
import CreateAppView from '../CreateAppView/CreateAppView';
import './AppsPage.css';

// Organization interface
interface Organization {
  id: string;
  orgId: string;
  orgName: string;
  country: string;
  countryCode: string;
  currencyCode: string;
  region: string;
  availableTimezones: string[];
  currencySymbol: string;
  stripeAccount: string;
  _metadata: {
    createdAt: {
      _seconds: number;
      _nanoseconds: number;
    };
    updatedAt: {
      _seconds: number;
      _nanoseconds: number;
    };
  };
}

// Basic App interface for this new system
interface App {
  id: string;
  name: string;
  description?: string;
  organizationId: string;
  createdBy: string;
  createdAt: any;
  status?: string;
  type?: string;
  enabled?: boolean;
  region?: string;
  appIcon?: string;
  decommissioned?: boolean;
}

interface AppsPageProps {
  db: Firestore;
  functions: Functions;
  organizationId: string;
  setOrganizationId: (id: string) => void;
  organizations: Record<string, Organization>;
  onOrganizationChange: (orgId: string) => void;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
}

// LocalStorage key for storing selected organization
const SELECTED_ORG_KEY = 'dineplatform_selected_organization';

const AppsPage: React.FC<AppsPageProps> = ({
  db,
  functions,
  organizationId,
  setOrganizationId,
  organizations,
  onOrganizationChange,
  onError,
  onSuccess,
  setLoading
}) => {
  const { currentUser } = useAuth();
  const [apps, setApps] = useState<App[]>([]);
  const [localLoading, setLocalLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState<App | null>(null);
  const [showCreateApp, setShowCreateApp] = useState(false);
  const [openMenuAppId, setOpenMenuAppId] = useState<string | null>(null);

  // Save organization to localStorage whenever it changes
  useEffect(() => {
    if (organizationId && organizations[organizationId]) {
      console.log('Saving organization to localStorage:', organizationId);
      localStorage.setItem(SELECTED_ORG_KEY, organizationId);
    }
  }, [organizationId, organizations]);

  // Load apps from Firestore
  const loadApps = async () => {
    if (!currentUser?.email || !organizationId) {
      setLocalLoading(false);
      return;
    }

    setLocalLoading(true);
    setLoading(true);

    try {
      // Query apps where organizationId matches and createdBy is current user
      const appsQuery = query(
        collection(db, 'apps'),
        where('organizationId', '==', organizationId),
        where('createdBy', '==', currentUser.email),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(appsQuery);
      const appsData: App[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // Only include apps that are not decommissioned
        if (data.decommissioned !== true) {
          appsData.push({
            id: doc.id,
            name: data.name || 'Untitled App',
            description: data.description || '',
            organizationId: data.organizationId || '',
            createdBy: data.createdBy || '',
            createdAt: data.createdAt,
            status: data.status || 'unknown',
            type: data.type || 'single-store',
            enabled: data.enabled ?? true,
            region: data.region || '',
            appIcon: data.appIcon || '',
            decommissioned: data.decommissioned || false
          });
        }
      });

      setApps(appsData);
      console.log(`Loaded ${appsData.length} active apps for organization ${organizationId}`);

    } catch (error) {
      console.error('Error loading apps:', error);
      onError('Failed to load apps. Please try again.');
    } finally {
      setLocalLoading(false);
      setLoading(false);
    }
  };

  // Load apps when organization changes or user changes
  useEffect(() => {
    if (currentUser?.email && organizationId) {
      loadApps();
    } else {
      setLocalLoading(false);
      setLoading(false);
    }
  }, [currentUser?.email, organizationId]);

  // Handle organization change from dropdown
  const handleOrganizationChange = (newOrgId: string) => {
    console.log('Organization changed to:', newOrgId);
    onOrganizationChange(newOrgId);
  };

  // Handle app card click
  const handleAppClick = (app: App) => {
    console.log('App clicked:', app);
    setSelectedApp(app);
  };

  // Handle back from control panel
  const handleBackFromControlPanel = () => {
    console.log('Back button clicked');
    setSelectedApp(null);
  };

  // Handle create app
  const handleCreateApp = () => {
    setShowCreateApp(true);
  };

  // Handle back from create app
  const handleBackFromCreateApp = () => {
    setShowCreateApp(false);
  };

  // Handle app created
  const handleAppCreated = () => {
    setShowCreateApp(false);
    loadApps(); // Refresh the apps list
  };

  // Handle delete app
  const handleDeleteApp = async (e: React.MouseEvent, app: App) => {
    e.stopPropagation(); // Prevent card click event
    setOpenMenuAppId(null); // Close menu

    const userInput = window.prompt(
      `⚠️ WARNING: This action cannot be undone!\n\nTo decommission "${app.name}", please type the app ID below to confirm:\n\n${app.id}`
    );

    // User cancelled or entered nothing
    if (userInput === null || userInput.trim() === '') {
      return;
    }

    // Check if the input matches the app ID
    if (userInput.trim() !== app.id) {
      onError('App ID does not match. Decommission cancelled.');
      return;
    }

    setLoading(true);

    try {
      console.log('Decommissioning app:', app.id);

      const decommissionAppFunction = httpsCallable(functions, 'decommissionApp');
      const result = await decommissionAppFunction({ appId: app.id });

      console.log('Decommission result:', result.data);

      onSuccess(`Successfully decommissioned ${app.name}`);
      
      // Reload apps to remove the decommissioned app from the list
      await loadApps();

    } catch (error: any) {
      console.error('Error decommissioning app:', error);
      
      let errorMessage = 'Failed to decommission app. Please try again.';
      if (error.code === 'permission-denied') {
        errorMessage = 'You do not have permission to decommission this app.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Toggle menu for an app
  const toggleMenu = (e: React.MouseEvent, appId: string) => {
    e.stopPropagation();
    setOpenMenuAppId(openMenuAppId === appId ? null : appId);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (openMenuAppId) {
        setOpenMenuAppId(null);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openMenuAppId]);

  // Add debug logging for selectedApp state
  useEffect(() => {
    console.log('selectedApp state changed:', selectedApp);
  }, [selectedApp]);

  // Show loading state
  if (localLoading) {
    return <LoadingPage message="Loading your apps..." />;
  }

  // Show authentication required state
  if (!currentUser?.email) {
    return (
      <div className="apps-auth-required">
        <h2>Authentication Required</h2>
        <p>Please log in to view your apps.</p>
      </div>
    );
  }

  // Show create app view
  if (showCreateApp) {
    return (
      <CreateAppView
        functions={functions}
        organizationId={organizationId}
        organizations={organizations}
        onError={onError}
        onSuccess={onSuccess}
        setLoading={setLoading}
        onBack={handleBackFromCreateApp}
        onAppCreated={handleAppCreated}
      />
    );
  }

  // Show control panel if app is selected
  if (selectedApp) {
    console.log('Rendering ControlPanel for app:', selectedApp);
    return (
      <ControlPanel
        db={db}
        functions={functions}
        appData={selectedApp}
        organizationId={organizationId}
        onBack={handleBackFromControlPanel}
        onError={onError}
        onSuccess={onSuccess}
        setLoading={setLoading}
      />
    );
  }

  const selectedOrg = organizations[organizationId];

  return (
    <div className="apps-page">
      {/* Header with organization selector, title and add button */}
      <div className="apps-header">
        <div className="apps-header-left">
          <h1>My Sites</h1>
          {selectedOrg && (
            <div className="organization-info">
              <span className="organization-name">{selectedOrg.orgName}</span>
              <span className="organization-region">{selectedOrg.region}</span>
            </div>
          )}
        </div>
        
        <div className="apps-header-right">
          {/* Organization Selector */}
          <div className="organization-selector">
            <label htmlFor="org-select">Organization:</label>
            <select
              id="org-select"
              value={organizationId}
              onChange={(e) => handleOrganizationChange(e.target.value)}
              className="org-dropdown"
            >
              {Object.entries(organizations).map(([orgId, org]) => (
                <option key={orgId} value={orgId}>
                  {org.orgName} ({org.region})
                </option>
              ))}
            </select>
          </div>
          
          <button className="apps-add-btn" onClick={handleCreateApp}>
            <span className="apps-add-btn-icon">+</span>
            Add Site
          </button>
        </div>
      </div>

      {/* Scrollable content area */}
      <div className="apps-content">
        {apps.length === 0 ? (
          <div className="apps-empty-state">
            <div className="apps-empty-state-icon">🌐</div>
            <h3>Create Your First Site</h3>
            <p>Get started by creating your first ordering site in {selectedOrg?.orgName || 'this organization'}.</p>
            <button className="apps-empty-state-btn" onClick={handleCreateApp}>
              Create Your First Site
            </button>
          </div>
        ) : (
          <div className="apps-grid">
            {apps.map((app) => (
              <div 
                key={app.id} 
                className="app-card"
                onClick={() => handleAppClick(app)}
              >
                <button
                  className="app-card-menu-btn"
                  onClick={(e) => toggleMenu(e, app.id)}
                  title="App options"
                  aria-label={`Options for ${app.name}`}
                >
                  ⋮
                </button>
                
                {openMenuAppId === app.id && (
                  <div className="app-card-menu">
                    <button
                      className="app-card-menu-item app-card-menu-item-delete"
                      onClick={(e) => handleDeleteApp(e, app)}
                    >
                      <span className="app-card-menu-icon">🗑️</span>
                      Decommission App
                    </button>
                  </div>
                )}
                
                <div className="app-card-content">
                  <div className="app-card-id">{app.id}</div>
                  <div className="app-card-name">{app.name}</div>
                </div>
                <div className="app-card-type">{app.type || 'Unknown'}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AppsPage;